/**
 * useScrollRestoration — preserves window scroll position per pathname.
 *
 * Usage:
 *   const Events = () => {
 *     useScrollRestoration({ ready: !loading });
 *     return ...;
 *   };
 *
 * Behaviour:
 *   - On unmount: stash window.scrollY in a module-level Map keyed by pathname
 *   - On mount: if a value is cached AND `ready` is true, scrollTo it on the
 *     next animation frame so the new content has had a chance to lay out
 *   - `ready` lets callers wait for their async data to render before
 *     restoring scroll — otherwise scrolling to "y=4000" on an empty page
 *     just snaps to the bottom of nothing
 *
 * Iter-86: introduced so navigating back from an event detail returns the
 * user to the same position in the events list. Designed to be reusable on
 * any long list page that loses scroll on route change.
 */
import { useEffect } from "react";
import { useLocation } from "react-router-dom";

const scrollCache = new Map(); // pathname -> scrollY (number)

export const useScrollRestoration = ({ ready = true } = {}) => {
  const location = useLocation();
  const pathname = location.pathname;

  // Restore on mount + whenever `ready` flips true (data finished loading)
  useEffect(() => {
    if (!ready) return;
    const saved = scrollCache.get(pathname);
    if (saved !== undefined && saved > 0) {
      // Two frames: one for layout, one for paint
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          window.scrollTo(0, saved);
        });
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname, ready]);

  // Save on unmount (component leaves view) AND on every scroll (so it's
  // current even if the user closes the tab / refreshes mid-scroll).
  useEffect(() => {
    const handler = () => {
      scrollCache.set(pathname, window.scrollY);
    };
    window.addEventListener("scroll", handler, { passive: true });
    return () => {
      // One last save on unmount
      scrollCache.set(pathname, window.scrollY);
      window.removeEventListener("scroll", handler);
    };
  }, [pathname]);
};

export default useScrollRestoration;
